import React, { memo, useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import routes from "@/routes/routes";
import styles from "./Navbar.module.scss";
import { Avatar, Button } from "@mui/material";
import FilterIcon from "@/components/SVGComponents/FilterIcon";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import { useAppDispatch } from "@/store/hooks";
import { setModalOpen } from "@/store/slices/globalSlice";
import { MODAL_ID } from "@/lib/constants";
import ExportIcon from "@/components/SVGComponents/NavbarIcons/ExportIcon";
import SendMessageIcon from "@/components/SVGComponents/NavbarIcons/SendMessageIcon";
import ProfileModal from "./components/ProfileModal/ProfileModal";
import UpdatePassword from "./components/UpdatePassword/UpdatePassword";
import { combineClasses } from "@/lib/utils";
import SearchInput from "@/components/SearchInput/SearchInput";

// Make sure label is string for react-select
export interface SelectOption {
  label: string;
  value: string;
}

interface NavbarProps {
  className: string;
}

const Navbar: React.FC<NavbarProps> = ({ className }) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { adminId } = useParams();
  const exportParams = Object.fromEntries(params.entries());

  const { pageNo, ...newSearchParams } = exportParams;


  const removeLastEndpoint = (str: string): string =>
    str.split("/").slice(0, -1).join("/");

  const handleAddClick = () => {
    if (navbarCom?.addbuttonPath) {
      navigate(navbarCom.addbuttonPath);
    } else {
      dispatch(setModalOpen({ id: MODAL_ID.ADD_MODAL }));
    }
  };

  const handleFilterClick = () => {
    dispatch(setModalOpen({ id: MODAL_ID.APPLY_FILTER }));
  };

  const handleSendMessage = () => {
    dispatch(setModalOpen({ id: MODAL_ID.SEND_MESSAGE }));
  };

  const handleEditUser = () => {
    dispatch(setModalOpen({ id: MODAL_ID.USER_EDIT }));
  };

  const handleClearSearch = () => {
    setSearch("");
  };

  const openLogoutModal = () => {
    dispatch(setModalOpen({ id: MODAL_ID.LOGOUT }));
  };

  const navbarCom = useMemo(() => {
    const allRoutes = Object.values(routes.privateRoutes).flatMap(
      (route: any) =>
        route?.children
          ? Object.values(route.children).map((child: any) => ({
            ...child,
            path: `/${route.path}/${child.path}`.replace(/\/+/g, "/"),
          }))
          : [{ ...route, path: `/${route.path}`.replace(/\/+/g, "/") }],
    );

    const currentRoute = allRoutes.find((pathData) => {
      const normalizedPath = pathData.path.replace(/\/+$/, "");
      const normalizedLocation = location.pathname.replace(/\/+$/, "");

      const lastSegment = normalizedPath.split("/").pop() || "";
      return (
        normalizedPath === normalizedLocation ||
        (lastSegment.startsWith(":") &&
          removeLastEndpoint(normalizedPath) ===
          removeLastEndpoint(normalizedLocation))
      );
    });

    return currentRoute?.navbarCom || {};
  }, [location]);

  const labelButton =
    typeof navbarCom.addbuttonLabel === "function"
      ? navbarCom.addbuttonLabel()
      : navbarCom.addbuttonLabel;

  const pageName = useMemo(() => {
    const normalizePath = (path: string) =>
      `/${path}`.replace(/\/+/g, "/").replace(/\/+$/, "");

    const allRoutes = Object.values(routes.privateRoutes).flatMap(
      (route: any) =>
        route?.children
          ? Object.values(route.children).map((child: any) => ({
            ...child,
            path: normalizePath(`${route.path}/${child.path}`),
          }))
          : [{ ...route, path: normalizePath(route.path) }],
    );

    const currentPath = normalizePath(location.pathname);

    const matchedRoute = allRoutes.find((route) => {
      const routePath = normalizePath(route.path);
      const lastSegment = routePath.split("/").pop() || "";
      return (
        routePath === currentPath ||
        (lastSegment.startsWith(":") &&
          removeLastEndpoint(routePath) === removeLastEndpoint(currentPath))
      );
    });

    return matchedRoute?.pageName || "";
  }, [location]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newParams = new URLSearchParams(params);

      if (search && search.trim() !== "") {
        newParams.set("search", search.toString().trim());
      } else {
        newParams.delete("search");
      }

      navigate({ search: newParams.toString() }, { replace: true });
    }, 500);

    return () => clearTimeout(timer);
  }, [search, params, navigate]);

  useEffect(() => {
    return () => setSearch("");
  }, [location.pathname]);

  const user = useMemo(() => {
    const userData = localStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  }, []);


  return (
    <div className={combineClasses(styles.topBarContainer, className)}>
      {/* <ProfileModal title="Profile" /> */}
      <UpdatePassword title="Update Password" />
      <div className={styles.leftSide}>
        <p className={styles.pageName}>{pageName || ""}</p>
      </div>

      <div className={styles.rightSide}>
        {navbarCom?.search && (
          <div className={styles.item}>
            <SearchInput
              value={search}
              onChange={setSearch}
              onClear={handleClearSearch}
            />
          </div>
        )}

        {navbarCom?.filter && (
          <div className={styles.item}>
            <Button
              disableElevation={true}
              className={
                navbarCom?.addbuttonLabel
                  ? styles.borderFilterBtn
                  : styles.filterBtn
              }
              startIcon={
                <FilterIcon
                  height={14}
                  width={14}
                  strokeColor={navbarCom?.addbuttonLabel ? "#374C44" : "white"}
                />
              }
              variant="contained"
              onClick={handleFilterClick}
            >
              Filter
            </Button>
          </div>
        )}

        {labelButton && (
          <div className={styles.item}>
            <Button
              disableElevation={true}
              className={styles.addButton}
              variant="contained"
              startIcon={<PlusIcon className={styles.plusicon} />}
              onClick={handleAddClick}
            >
              {labelButton}
            </Button>
          </div>
        )}

        {navbarCom?.export && (
          <div className={styles.item}>
            <Button
              disableElevation={true}
              onClick={() => navbarCom.export(newSearchParams)}
              variant="contained"
              className={styles.exportBtn}
              startIcon={<ExportIcon />}
            >
              Export
            </Button>
          </div>
        )}

        {navbarCom?.sendMessage && (
          <div className={styles.item}>
            <Button
              disableElevation={true}
              onClick={handleSendMessage}
              variant="contained"
              className={styles.sendMessageBtn}
            >
              <SendMessageIcon />
              Send Message
            </Button>
          </div>
        )}

        <div className={styles.item}>
          <div className={styles.logoutCircle}>
            <div className={styles.userInfo}>
              <p className={styles.userName}>{user?.fullName || "User"}</p>
              <p className={styles.userRole}>{user?.Role?.name || "Admin"}</p>
            </div>
            <Avatar
              onClick={handleEditUser}
              sx={{ width: "3.1rem", height: "3.1rem", cursor: "pointer" }}
              src={user?.profilePicture || "/Profile.png"}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Navbar);
